import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import '../styles/components/LoginModal.css';

const API_URL = process.env.REACT_APP_API_URL;

// 인플루언서 선캐시(계약/통장/프로필/SNS 보기/SNS 삭제 목록/진행중 문의/완료 문의)
// opts.blockingMs 안에서 핵심(계약/SNS보기/삭제/진행중/완료 문의)만 짧게 대기, 나머지는 백그라운드
async function preCacheInfluencerData(initialUserKey, opts = {}) {
  const withBudget = (promises, ms = 0) =>
    ms > 0
      ? Promise.race([Promise.allSettled(promises), new Promise(res => setTimeout(res, ms))])
      : Promise.allSettled(promises);

  // 1) 사용자키 확정
  let uk = String(initialUserKey || '').toLowerCase();
  try {
    const meRes = await fetch(`${API_URL}/api/user/me`, { credentials: 'include' });
    const me = await meRes.json().catch(() => ({}));
    if (meRes.ok && me?.ok) uk = String(me.user?.userId || uk).toLowerCase();
  } catch {}
  if (!uk) return;
  try { sessionStorage.setItem('current_user_key', uk); } catch {}

  // 2) 키들
  const k = {
    contractStatus: `influencer_contract_status:${uk}`,
    contractLast:   `influencer_contract_last:${uk}`,
    bankExists: `influencer_bank_exists:${uk}`,
    bankHolder: `influencer_bank_holder:${uk}`,
    bankName:   `influencer_bank_name:${uk}`,
    bankMasked: `influencer_bank_masked:${uk}`,
    profName:  `influencer_profile_name:${uk}`,
    profPhone: `influencer_profile_phone:${uk}`,
    profEmail: `influencer_profile_email:${uk}`,
    profAddr:  `influencer_profile_addr:${uk}`,
    snsGroups: `influencer_sns_groups:${uk}`,
    snsRemove: `influencer_sns_remove:${uk}`,
    inquiryOpen: `inquiry_open__${uk}`,
    inquiryDone: `inquiry_done__${uk}`
  };

  // 3) 핵심(초기 화면 깜빡임 방지에 필요한 것들)과 비핵심(백그라운드)
  const critical = [
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/influencer/mybiz/working-contract/status`, { credentials: 'include' });
        const j = await r.json().catch(()=>({}));
        if (r.ok && j?.ok) {
          localStorage.setItem(k.contractStatus, j.status || 'none');
          if (j.lastSentAt) localStorage.setItem(k.contractLast, String(Number(j.lastSentAt)));
        } else {
          localStorage.removeItem(k.contractStatus);
          localStorage.removeItem(k.contractLast);
        }
      } catch {}
    })(),
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/influencer/mybiz/sns-list`, { credentials: 'include' });
        const j = await r.json().catch(()=>({}));
        if (r.ok && j?.ok) {
          const next = {
            instagram: j.groups?.instagram || [],
            blog:      j.groups?.blog      || [],
            youtube:   j.groups?.youtube   || [],
            tiktok:    j.groups?.tiktok    || [],
          };
          localStorage.setItem(k.snsGroups, JSON.stringify(next));
        } else {
          localStorage.removeItem(k.snsGroups);
        }
      } catch {}
    })(),
    // 진행중인 문의 선캐시
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/influencer/mybiz/inquiry/open`, { credentials: 'include' });
        const j = await r.json().catch(()=>({}));
        if (r.ok && j?.ok) {
          localStorage.setItem(k.inquiryOpen, JSON.stringify(j.items || []));
        } else {
          localStorage.removeItem(k.inquiryOpen);
        }
      } catch {}
    })(),
    // 완료된 문의 선캐시
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/influencer/mybiz/inquiry/done`, { credentials: 'include' });
        const j = await r.json().catch(()=>({}));
        if (r.ok && j?.ok) {
          localStorage.setItem(k.inquiryDone, JSON.stringify(j.items || []));
        } else {
          localStorage.removeItem(k.inquiryDone);
        }
      } catch {}
    })(),
  ];

  const nonCritical = [
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/influencer/mybiz/bank`, { credentials: 'include' });
        const j = await r.json().catch(()=>({}));
        if (r.ok && j?.ok && j?.exists && j?.bank) {
          const holder = j.bank.holderName || '';
          const bname  = j.bank.bankName || '';
          const acc    = String(j.bank.accountNumber || '');
          const digits = acc.replace(/\D/g, '');
          const masked = digits.length > 6 ? digits.slice(0,6) + digits.slice(6).replace(/./g,'*') : digits.replace(/./g,'*');
          localStorage.setItem(k.bankExists, '1');
          localStorage.setItem(k.bankHolder, holder);
          localStorage.setItem(k.bankName,   bname);
          localStorage.setItem(k.bankMasked, masked);
        } else {
          localStorage.removeItem(k.bankExists);
          localStorage.removeItem(k.bankHolder);
          localStorage.removeItem(k.bankName);
          localStorage.removeItem(k.bankMasked);
        }
      } catch {}
    })(),
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/influencer/mybiz/profile`, { credentials: 'include' });
        const j = await r.json().catch(()=>({}));
        if (r.ok && j?.ok) {
          localStorage.setItem(k.profName,  j.userName || '');
          localStorage.setItem(k.profPhone, j.phoneNumber || '');
          localStorage.setItem(k.profEmail, j.email || '');
          localStorage.setItem(k.profAddr,  j.userAddress || '');
        } else {
          localStorage.removeItem(k.profName);
          localStorage.removeItem(k.profPhone);
          localStorage.removeItem(k.profEmail);
          localStorage.removeItem(k.profAddr);
        }
      } catch {}
    })(),
  ];

  const budget = Number(opts.blockingMs ?? 400);
  void Promise.allSettled(nonCritical);   // 비핵심은 백그라운드에서 처리
  await withBudget(critical, budget);
}

// 광고주(마케터) 선캐시: 진행중/완료 문의
async function preCacheMarketerData(initialUserKey, opts = {}) {
  const withBudget = (promises, ms = 0) =>
    ms > 0
      ? Promise.race([Promise.allSettled(promises), new Promise(res => setTimeout(res, ms))])
      : Promise.allSettled(promises);

  let uk, uid;
  
  // ✅ skipMeCheck가 true면 /api/user/me 호출 안 함! (중복 호출 방지)
  if (opts.skipMeCheck && initialUserKey) {
    uid = initialUserKey;
    uk = String(initialUserKey || '').toLowerCase();
  } else {
    // 기존 로직 그대로
    uk = String(initialUserKey || '').toLowerCase();
    try {
      const meRes = await fetch(`${API_URL}/api/user/me`, { credentials: 'include' });
      const me = await meRes.json().catch(() => ({}));
      if (meRes.ok && me?.ok) {
        uk = String(me.user?.userId || uk).toLowerCase();
        uid = me.user?.id;
      }
    } catch {}
  }
  
  if (!uk && !uid) return;
  try { sessionStorage.setItem('current_user_key', uk); } catch {}

  const k = {
    inquiryOpen: `marketer_inquiry_open__${uk}`,
    inquiryDone: `marketer_inquiry_done__${uk}`,
  };

  const critical = [
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/marketer/mybiz/inquiry/open`, { credentials: 'include' });
        const j = await r.json().catch(()=>({}));
        if (r.ok && j?.ok) localStorage.setItem(k.inquiryOpen, JSON.stringify(j.items || []));
        else localStorage.removeItem(k.inquiryOpen);
      } catch {}
    })(),
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/marketer/mybiz/inquiry/done`, { credentials: 'include' });
        const j = await r.json().catch(()=>({}));
        if (r.ok && j?.ok) localStorage.setItem(k.inquiryDone, JSON.stringify(j.items || []));
        else localStorage.removeItem(k.inquiryDone);
      } catch {}
    })(),
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/marketer/mybiz/points/pending-list`, { credentials: 'include' });
        const j = await r.json().catch(()=>({}));
        const pk = `mrk_pending__${uk}`;
        try {
          sessionStorage.removeItem('mrk_pending');        // 레거시 제거
          sessionStorage.removeItem('mrk_pending_cache');  // 레거시 제거
        } catch {}
        if (r.ok && j?.ok) {
          sessionStorage.setItem(pk, JSON.stringify(j.items || []));
        } else {
          sessionStorage.removeItem(pk);
        }
      } catch {}
    })(),
  ];

  // ✅✅ nonCritical: 백그라운드에서 조용히 실행 (로그인 완료를 막지 않음)
  const nonCritical = [
    // ✅ 프로젝트 목록 + 첫 지원자 프리페치
    (async () => {
      if (!uid) return;
      try {
        const r = await fetch(`${API_URL}/api/marketer/projects/with-applications`, { 
          credentials: 'include' 
        });
        const j = await r.json().catch(() => ({}));
        if (r.ok && j?.ok && j.projects) {
          const cacheKey = `MARKETER_PROJECT_LIST:${uid}`;
          const cacheData = {
            data: j.projects,
            timestamp: Date.now(),
            uid: uid
          };
          try {
            sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
            console.log(`✅ 로그인 시 프로젝트 목록 프리페치 완료 (${j.projects.length}개)`);
            
            // ✅✅ 첫 프로젝트의 지원자 목록도 프리페치
            if (j.projects.length > 0) {
              const firstProjectId = j.projects[0].id;
              if (firstProjectId) {
                setTimeout(async () => {
                  try {
                    const appRes = await fetch(
                      `${API_URL}/api/marketer/projects/${firstProjectId}/applications?filter=all`,
                      { credentials: 'include' }
                    );
                    const appData = await appRes.json().catch(() => ({}));
                    if (appRes.ok && appData.ok && appData.applications) {
                      const appCacheKey = `MARKETER_PROJECT_APP:${firstProjectId}:all`;
                      const appCacheData = {
                        data: appData.applications,
                        timestamp: Date.now(),
                        uid: uid
                      };
                      sessionStorage.setItem(appCacheKey, JSON.stringify(appCacheData));
                      console.log(`✅ 로그인 시 첫 프로젝트 지원자 프리페치 완료 (${appData.applications.length}명)`);
                    }
                  } catch (e) {
                    console.warn('첫 프로젝트 지원자 프리페치 실패:', e);
                  }
                }, 100); // 프로젝트 목록 저장 후 실행
              }
            }
          } catch (e) {
            console.warn('프로젝트 목록 캐시 저장 실패:', e);
          }
        }
      } catch (e) {
        console.warn('프로젝트 목록 프리페치 실패:', e);
      }
    })(),
    
    // ✅ 결과물 관리 프로젝트 목록 + 첫 결과물 프리페치
    (async () => {
      if (!uid) return;
      try {
        const r = await fetch(`${API_URL}/api/marketer/projects/with-results`, { 
          credentials: 'include' 
        });
        const j = await r.json().catch(() => ({}));
        if (r.ok && j?.ok && j.projects) {
          const cacheKey = `MARKETER_RESULT_LIST:${uid}`;
          const cacheData = {
            data: j.projects,
            timestamp: Date.now(),
            uid: uid
          };
          try {
            sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
            console.log(`✅ 로그인 시 결과물 프로젝트 목록 프리페치 완료 (${j.projects.length}개)`);
            
            // ✅✅ 첫 프로젝트의 결과물 목록도 프리페치
            if (j.projects.length > 0) {
              const firstProjectId = j.projects[0].id;
              if (firstProjectId) {
                setTimeout(async () => {
                  try {
                    const resRes = await fetch(
                      `${API_URL}/api/marketer/projects/${firstProjectId}/results?filter=all`,
                      { credentials: 'include' }
                    );
                    const resData = await resRes.json().catch(() => ({}));
                    if (resRes.ok && resData.ok && resData.results) {
                      const resCacheKey = `MARKETER_RESULT_RES:${firstProjectId}:all`;
                      const resCacheData = {
                        data: resData.results,
                        timestamp: Date.now(),
                        uid: uid
                      };
                      sessionStorage.setItem(resCacheKey, JSON.stringify(resCacheData));
                      console.log(`✅ 로그인 시 첫 프로젝트 결과물 프리페치 완료 (${resData.results.length}건)`);
                    }
                  } catch (e) {
                    console.warn('첫 프로젝트 결과물 프리페치 실패:', e);
                  }
                }, 200); // 프로젝트 목록 저장 후 실행
              }
            }
          } catch (e) {
            console.warn('결과물 프로젝트 목록 캐시 저장 실패:', e);
          }
        }
      } catch (e) {
        console.warn('결과물 프로젝트 목록 프리페치 실패:', e);
      }
    })(),
    
    // ✅ CPS 결산 프로젝트 목록 + 첫 프로젝트 상세 프리페치
    (async () => {
      if (!uid) return;
      try {
        const r = await fetch(`${API_URL}/api/marketer/cps-settlements`, { 
          credentials: 'include' 
        });
        const j = await r.json().catch(() => ({}));
        if (r.ok && j?.ok && j.projects) {
          const cacheKey = `MARKETER_CPS_LIST:${uid}`;
          const cacheData = {
            data: j.projects,
            timestamp: Date.now(),
            uid: uid
          };
          try {
            sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
            console.log(`✅ 로그인 시 CPS 프로젝트 목록 프리페치 완료 (${j.projects.length}개)`);
            
            // ✅✅ 첫 프로젝트의 CPS 상세도 프리페치
            if (j.projects.length > 0) {
              const firstProjectId = j.projects[0].id;
              if (firstProjectId) {
                setTimeout(async () => {
                  try {
                    const detailRes = await fetch(
                      `${API_URL}/api/marketer/cps-settlements/${firstProjectId}`,
                      { credentials: 'include' }
                    );
                    const detailData = await detailRes.json().catch(() => ({}));
                    if (detailRes.ok && detailData.ok) {
                      const detailCacheKey = `MARKETER_CPS_DETAIL:${firstProjectId}`;
                      const detailCacheData = {
                        data: detailData,
                        timestamp: Date.now(),
                        uid: uid
                      };
                      sessionStorage.setItem(detailCacheKey, JSON.stringify(detailCacheData));
                      console.log(`✅ 로그인 시 첫 CPS 프로젝트 상세 프리페치 완료`);
                    }
                  } catch (e) {
                    console.warn('첫 CPS 프로젝트 상세 프리페치 실패:', e);
                  }
                }, 300); // CPS 목록 저장 후 실행
              }
            }
          } catch (e) {
            console.warn('CPS 프로젝트 목록 캐시 저장 실패:', e);
          }
        }
      } catch (e) {
        console.warn('CPS 프로젝트 목록 프리페치 실패:', e);
      }
    })(),
  ];

  const budget = Number(opts.blockingMs ?? 300);
  
  // ✅ critical만 대기, nonCritical은 백그라운드 실행
  void Promise.allSettled(nonCritical);
  await withBudget(critical, budget);
}

function LoginModal({ onClose, dismissOnBackdrop = false }) {
  const navigate = useNavigate();
  const [userId, setUserId] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.paddingRight = `${scrollbarWidth}px`;
    document.body.classList.add('modal-open');
    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.paddingRight = '';
    };
  }, []);

  const handleGoRegister = () => navigate('/register');

  const login = async () => {
    setMsg('');
    if (!userId.trim() || !userPassword.trim()) {
      setMsg('아이디와 비밀번호를 입력하세요.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId, userPassword })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMsg(data.message || '로그인 실패');
        setLoading(false);
        return;
      }
      const role = data.role;

      try {
        const typedUk = String(userId).toLowerCase();
        sessionStorage.setItem('current_user_key', typedUk);
        sessionStorage.setItem('current_user_role', role);
      } catch {}

      if (role === 'influencer') {
        const typedUk = String(userId).toLowerCase();
        try { sessionStorage.setItem('current_user_key', typedUk); } catch {}
        
        // window.__ME__ 설정 및 warmup 재시작
        try {
          const meRes = await fetch('/api/user/me', { credentials: 'include' });
          const meData = await meRes.json().catch(() => ({}));
          if (meRes.ok && meData?.ok && meData.role === 'influencer') {
            const uid = meData?.user?.id || meData?.id;
            if (uid) {
              window.__ME__ = { id: uid, role: 'influencer' };
              if (window.warmupInfluencer) {
                setTimeout(() => window.warmupInfluencer(), 0);
              }
            }
          }
        } catch {}
        
        await preCacheInfluencerData(typedUk, { blockingMs: 400 });
        navigate('/influencer/mybiz');
      } else if (role === 'marketer') {
        const typedUk = String(userId).toLowerCase();
        try { sessionStorage.setItem('current_user_key', typedUk); } catch {}
        
        // ✅ /api/user/me를 한 번만 호출 (중복 방지)
        let uid = null;
        try {
          const meRes = await fetch('/api/user/me', { credentials: 'include' });
          const meData = await meRes.json().catch(() => ({}));
          if (meRes.ok && meData?.ok && meData.role === 'marketer') {
            uid = meData?.user?.id || meData?.id;
            if (uid) {
              window.__ME__ = { id: uid, role: 'marketer' };
              
              // ✅ 이벤트 발송
              window.dispatchEvent(new CustomEvent('user-ready', { 
                detail: window.__ME__ 
              }));
              
              // ✅ warmup 실행
              if (window.warmupMarketer) {
                window.warmupMarketer();
              }
            }
          }
        } catch {}
        
        // ✅ preCacheMarketerData에 uid와 skipMeCheck 전달 (중복 /api/user/me 호출 방지)
        await preCacheMarketerData(uid || typedUk, { 
          blockingMs: 300, 
          skipMeCheck: !!uid  // uid가 있으면 /api/user/me 스킵
        });
        
        navigate('/marketer/mybiz');
      } else {
        setMsg('알 수 없는 사용자 유형입니다.');
      }
      onClose && onClose();
    } catch {
      setMsg('서버 요청 실패');
    }
    setLoading(false);
  };

  const content = (
    <div className="login-modal" onClick={dismissOnBackdrop ? onClose : undefined}>
            <div className="login-modal__content" onClick={(e) => e.stopPropagation()}>
        <button className="login-modal__close-btn" onClick={onClose} aria-label="닫기">×</button>

        <div className="login-modal__logo-wrap">
          <img src="/images/logo.svg" alt="셀프애드" className="login-modal__logo" />
        </div>

        <div className="login-modal__field">
          <input
            className="login-modal__input"
            placeholder="아이디"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            disabled={loading}
          />
        </div>
        <div className="login-modal__field">
          <input
            className="login-modal__input"
            type="password"
            placeholder="비밀번호"
            value={userPassword}
            onChange={(e) => setUserPassword(e.target.value)}
            disabled={loading}
          />
        </div>

        <button
          className="login-modal__button login-modal__button--primary"
          onClick={login}
          disabled={loading}
        >
          {loading ? '로그인 중...' : '로그인'}
        </button>

        {msg && <div className="login-modal__error">{msg}</div>}

        <div className="login-modal__help">
          <button
            type="button"
            className="login-modal__link"
            onClick={() => navigate('/account/recovery?tab=id')}
            disabled={loading}
          >
            아이디 찾기
          </button>
          <span className="login-modal__help-sep">|</span>
          <button
            type="button"
            className="login-modal__link"
            onClick={() => navigate('/account/recovery?tab=pw')}
            disabled={loading}
          >
            비밀번호 찾기
          </button>
        </div>        

        <div className="login-modal__divider" />
        <div className="login-modal__actions-row">
          {/* 가입 선택 페이지(v5) 경유 — 역할별 니즈 카드에서 선택 */}
          <button className="login-modal__button" onClick={handleGoRegister} disabled={loading}>
            회원가입
          </button>
        </div>
            </div>
        </div>
    );

  return createPortal(content, document.body);
}

export default LoginModal;